<template>
  <div v-if="params" class="tsg-engine-controls-header">
    <!-- Sticky header name (mimics AG Grid's sticky label) -->
    <div class="ag-header-group-cell-label ag-sticky-label">
      <span class="ag-header-group-text">{{ params.headerName || '' }}</span>
    </div>

    <!-- Engine control buttons (scrollable) -->
    <feature-toggle :toggle="enableTSGLevelEngineRuns">
      <div
        v-if="!params.isUnitManagerView && params.toolStoreGroupKey && showEngineControls"
        class="engine-buttons-container"
      >
        <!-- Strategic engine column -->
        <div class="engine-column">
          <tooltip :value="strategicEngineTooltipContent" position="bottom-right">
            <v-btn
              depressed
              :loading="isThisTsgCalculatingStrategic"
              :disabled="
                !isEditable ||
                  !engineCanRun ||
                  calculatingScenarioElasticityResults ||
                  hasUnsavedKpiResults ||
                  isBlockedByOtherRun
              "
              :class="[
                'refresh-button',
                `${hasUnsavedKpiResults && !isThisTsgCalculatingStrategic ? 'unsaved-kpis' : ''}`,
              ]"
              @click="recalculateStrategic"
            >
              <cog-play-icon />
            </v-btn>
          </tooltip>

          <last-triggered-info
            v-if="lastTriggeredInfo.strategic_engine_full_run"
            :last-triggered-info="
              get(lastTriggeredInfo.strategic_engine_full_run, params.toolStoreGroupKey, {})
            "
            :loading="isLastTriggeredLoading.strategic_engine"
            source="strategic_engine_full_run"
            :width="80"
            simplified
          />
        </div>

        <!-- Cross-elasticity column -->
        <feature-toggle :toggle="enablePriceOptimisation">
          <div class="engine-column">
            <tooltip :value="crossElasticityEngineTooltipContent" position="bottom-right">
              <v-btn
                depressed
                :loading="isThisTsgCalculatingElasticity"
                :disabled="!canRunOptimisation"
                class="refresh-button cross-elasticity-button"
                @click="runCrossElasticityOptimisation"
              >
                <circles-play-icon />
              </v-btn>
            </tooltip>

            <last-triggered-info
              :last-triggered-info="
                get(lastTriggeredInfo.cross_elasticity_engine, params.toolStoreGroupKey, {})
              "
              :loading="isLastTriggeredLoading.cross_elasticity_engine"
              source="cross_elasticity_engine"
              translation-time-key="gridView.optimisationLastRunTime"
              :width="80"
              simplified
            />
          </div>
        </feature-toggle>
      </div>
    </feature-toggle>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { enableTSGLevelEngineRuns } from '@enums/feature-flags';
import featureFlagsMixin from '../mixins/featureFlags';
import engineControlsMixin from '../mixins/engineControls';

export default {
  name: 'TSGEngineControls',

  mixins: [featureFlagsMixin, engineControlsMixin],

  data() {
    return {
      engineCanRun: false,
      engineBlockedMsg: '',
      crossElasticityBlockedMsg: '',
      enableTSGLevelEngineRuns,
    };
  },

  computed: {
    ...mapState('gridView', [
      'calculatingTsgStrategicKeys',
      'calculatingTsgElasticityKeys',
      'canEngineRunResults',
    ]),

    /**
     * Whether this header instance is rendered inside the pinned panel.
     * AG Grid splits a column group into two displayed instances when some of its child
     * columns are pinned; this flag identifies the pinned-panel copy so we can hide the
     * engine control buttons there and avoid showing them twice.
     */
    isPinnedInstance() {
      return !!(this.params.columnGroup && this.params.columnGroup.getPinned());
    },

    /** Engine controls should only appear on the scrollable (non-pinned) instance. */
    showEngineControls() {
      return !this.isPinnedInstance;
    },

    /**
     * Whether the strategic engine is running specifically for this TSG.
     * Falls back to the global flag when no per-TSG keys are tracked (e.g. after a page refresh
     * while a scenario-level run is in progress).
     */
    isThisTsgCalculatingStrategic() {
      if (this.calculatingTsgStrategicKeys.length > 0) {
        return this.calculatingTsgStrategicKeys.includes(this.params.toolStoreGroupKey);
      }
      return this.calculatingScenarioResults;
    },

    /** Whether a different TSG's strategic engine is running. */
    isAnotherTsgCalculatingStrategic() {
      return (
        this.calculatingTsgStrategicKeys.length > 0 &&
        !this.calculatingTsgStrategicKeys.includes(this.params.toolStoreGroupKey)
      );
    },

    /**
     * Whether the cross-elasticity engine is running specifically for this TSG.
     * Falls back to the global flag when no per-TSG keys are tracked.
     */
    isThisTsgCalculatingElasticity() {
      if (this.calculatingTsgElasticityKeys.length > 0) {
        return this.calculatingTsgElasticityKeys.includes(this.params.toolStoreGroupKey);
      }
      return this.calculatingScenarioElasticityResults;
    },

    /** Whether a different TSG's cross-elasticity engine is running. */
    isAnotherTsgCalculatingElasticity() {
      return (
        this.calculatingTsgElasticityKeys.length > 0 &&
        !this.calculatingTsgElasticityKeys.includes(this.params.toolStoreGroupKey)
      );
    },

    /** Whether any other engine run (different TSG) blocks this TSG's buttons. */
    isBlockedByOtherRun() {
      return this.isAnotherTsgCalculatingStrategic || this.isAnotherTsgCalculatingElasticity;
    },

    engineButtonTooltipMsg() {
      if (this.hasUnsavedKpiResults) {
        return this.$t('gridView.tooltips.hasUnsavedKpiResultsScenario');
      }
      if (this.calculatingScenarioElasticityResults) {
        return this.$t('gridView.tooltips.crossElasticityOptimisationRunning');
      }
      return this.engineBlockedMsg;
    },

    crossElasticityButtonTooltipMsg() {
      if (this.isWeeklyWorkpackage) {
        return this.$t('gridView.tooltips.notAllowedInWeeklyWorkpackage');
      }
      if (this.hasUnsavedKpiResults) {
        return this.$t('gridView.tooltips.hasUnsavedKpiResultsScenario');
      }
      if (this.calculatingScenarioResults) {
        return this.$t('gridView.tooltips.engineRunning');
      }
      if (!this.hasAOExtracts) {
        return this.$t('gridView.tooltips.missingAOExtracts');
      }
      return this.crossElasticityBlockedMsg;
    },

    canRunOptimisation() {
      return (
        this.isEditable &&
        !this.isWeeklyWorkpackage &&
        !this.hasUnsavedKpiResults &&
        !this.calculatingScenarioResults &&
        this.hasAOExtracts &&
        !this.isBlockedByOtherRun
      );
    },

    strategicEngineTooltipContent() {
      const title = this.$t('gridView.tooltips.strategicOptimiser');
      const blockReason = this.engineButtonTooltipMsg;
      if (!blockReason) return title;
      if (typeof blockReason === 'string') return `${title} - ${blockReason}`;
      // Object case (engine cannot run): format key-value pairs as lines
      return [title, ...Object.entries(blockReason).map(([k, v]) => `${k}: ${v}`)].join(' - ');
    },

    crossElasticityEngineTooltipContent() {
      const title = this.$t('gridView.tooltips.crossElasticityOptimiser');
      const blockReason = this.crossElasticityButtonTooltipMsg;
      if (!blockReason) return title;
      return `${title} - ${blockReason}`;
    },
  },

  watch: {
    canEngineRunResults(newValue) {
      this.updateCanEngineRunInfo(newValue);
    },
  },

  created() {
    this.updateCanEngineRunInfo(this.canEngineRunResults);
  },

  methods: {
    async recalculateStrategic() {
      this.handleRecalculate({
        scenarioKey: this.scenario,
        workpackageId: this.selectedWorkpackage._id,
        toolStoreGroupKeys: [this.params.toolStoreGroupKey],
      });
    },

    async runCrossElasticityOptimisation() {
      this.handleCrossElasticityOptimisation({
        scenarioKey: this.scenario,
        workpackageId: this.selectedWorkpackage._id,
        toolStoreGroupKeys: [this.params.toolStoreGroupKey],
      });
    },

    updateCanEngineRunInfo(canEngineRunResults) {
      this.engineCanRun = canEngineRunResults.canEngineRun;
      if (!this.engineCanRun) {
        this.engineBlockedMsg = {
          [this.$t('pricing.error')]: this.$t('pricing.engineCannotRun', {
            pricingGroupName: this.pricingGroupName || '',
          }),
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.tsg-engine-controls-header {
  // Be careful when changing these to make sure sticky behaviour is preserved for tsg-key header label and buttons
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  position: sticky;
  left: 0;
  z-index: 1;
  background: inherit;
  width: fit-content;
  max-width: 100%;

  .ag-sticky-label {
    display: flex;
    align-items: center;
    padding-right: 1rem;
    margin-right: 0.5rem;

    .ag-header-group-text {
      font-weight: 800;
      white-space: nowrap;
    }
  }

  .engine-buttons-container {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;

    .engine-column {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .refresh-button {
    background-color: $pricing-primary !important;
    color: white;
    min-width: unset !important;
    padding: unset !important;
    height: 2rem !important;
    width: 2rem;

    &.unsaved-kpis {
      background-color: $pricing-orange !important;
    }
  }

  .cross-elasticity-button {
    background-color: $pricing-blue !important;
  }
}
</style>
